/**
 * TriSys API Service
 * Typed wrapper for TriSys REST API endpoints
 * 
 * Generated from: TriSys-2026.01.13.postman_collection.json
 * Date: 2026-01-14
 */

import { Logger } from '@/services/Logger';
import { trisysAuth } from './AuthService';
import type { 
  AuthResponse,
  Contact,
  ContactSearchParams, 
  ContactSearchResponse,
  ContactReadResponse,
  Company,
  CompanySearchParams,
  CompanySearchResponse,
  CompanyReadResponse
} from '../types';

const logger = Logger.scope('TriSysApiService');

/**
 * Get the API base URL.
 * Checks for a Vertical Stack Server endpoint redirect first (stored after authenticate),
 * then falls back to environment variable or production URL.
 */
function getBaseUrl(): string {
  const storedEndpoint = sessionStorage.getItem('trisys_api_endpoint');
  if (storedEndpoint) return storedEndpoint;
  return import.meta.env.VITE_TRISYS_API_URL || 'https://api.trisys.co.uk';
}

/**
 * TriSys API Service
 * Provides typed methods for all TriSys API endpoints
 */
class TriSysApiService {
  
  /**
   * Make an authenticated API request
   */
  private async request<T>(
    path: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${getBaseUrl()}${path}`;
    
    logger.debug('API Request', { method: options.method || 'GET', path });
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...trisysAuth.getHeaders(),
        ...options.headers
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('HTTP Error', { 
        status: response.status, 
        statusText: response.statusText,
        body: errorText 
      });
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Check for API-level errors (Success: false)
    if (data.Success === false) {
      logger.error('API Error', { 
        path, 
        errorMessage: data.ErrorMessage 
      });
      throw new Error(data.ErrorMessage || 'API request failed');
    }
    
    return data as T;
  }

  // ============================================================
  // Authentication
  // ============================================================

  /**
   * Authenticate user and establish session
   * @param email User's email address (passed as FullName in API)
   * @param password User's password
   * @returns The DataServicesKey for subsequent requests
   */
  async authenticate(email: string, password: string): Promise<string> {
    logger.debug('Authenticating', { email });
    
    const response = await fetch(`${getBaseUrl()}/security/authenticate`, {
      method: 'POST',
      headers: trisysAuth.getAuthHeaders(),
      body: JSON.stringify({
        FullName: email,
        Password: password
      })
    });
    
    if (!response.ok) {
      throw new Error(`Authentication failed: HTTP ${response.status}`);
    }
    
    const data: AuthResponse = await response.json();
    
    if (!data.Success) {
      throw new Error(data.ErrorMessage || 'Authentication failed');
    }
    
    // NOTE: Session is stored by Flexiva's central auth (TriSysWebAPI)
    // This method is kept for API compatibility but shouldn't be used directly
    
    logger.debug('Authentication successful');
    return data.DataServicesKey;
  }

  /**
   * Logout - delegates to Flexiva's central auth
   * NOTE: Use Flexiva's logout form instead of calling this directly
   */
  logout(): void {
    // Flexiva handles session clearing via its central auth
    logger.debug('Logout requested - use Flexiva logout form');
  }

  /**
   * Check if currently authenticated
   */
  isAuthenticated(): boolean {
    return trisysAuth.isAuthenticated();
  }

  // ============================================================
  // Contacts
  // ============================================================

  /**
   * Search contacts with pagination and filters
   * POST /contacts/search (legacy simple search)
   */
  async searchContacts(params: ContactSearchParams): Promise<ContactSearchResponse> {
    logger.debug('Searching contacts', params);
    
    return this.request<ContactSearchResponse>('/contacts/search', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  /**
   * Search contacts with Boolean Search Tree support
   * POST /Contacts/List - newer endpoint with SearchTreeJson support
   * 
   * @param params - Search parameters including optional SearchTreeEnabled/SearchTreeJson
   * @returns ContactSearchResponse with paginated results
   */
  async searchContactsWithTree(params: ContactSearchParams): Promise<ContactSearchResponse> {
    logger.debug('Searching contacts with tree', { 
      ...params,
      SearchTreeJson: params.SearchTreeJson ? '[SearchTree]' : undefined 
    });
    
    return this.request<ContactSearchResponse>('/Contacts/List', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  /**
   * Read a single contact by ID
   * POST /contact/read
   */
  async readContact(contactId: number): Promise<Contact> {
    logger.debug('Reading contact', { contactId });
    
    const response = await this.request<ContactReadResponse>('/contact/read', {
      method: 'POST',
      body: JSON.stringify({ ContactId: contactId })
    });
    
    return response.Contact;
  }

  // ============================================================
  // Future: Create, Update, Delete operations
  // ============================================================

  // async createContact(contact: Partial<Contact>): Promise<Contact> {
  //   // POST /contact/create
  // }

  // async updateContact(contactId: number, updates: Partial<Contact>): Promise<Contact> {
  //   // POST /contact/update
  // }

  // async deleteContact(contactId: number): Promise<void> {
  //   // POST /contact/delete
  // }

  // ============================================================
  // Companies
  // ============================================================

  /**
   * Transform API response item to CompanySummary
   * Raw API response item from /Companies/List
   * API returns prefixed field names like Company_CompanyId, Company_Name
   * Also passes through DynamicColumn_* fields for grid display
   */
  private transformCompanyListItem(item: Record<string, unknown>): import('../types').CompanySummary {
    const result: import('../types').CompanySummary = {
      CompanyId: item.Company_CompanyId as number,
      CompanyName: item.Company_Name as string,
      WebPage: (item.Company_WebPage as string) || null,
      MainTelNo: (item.Company_MainTelNo as string) || null,
      Industry: (item.Company_Industry as string) || null,
      Country: (item.Company_Country as string) || null,
      City: (item.Company_City as string) || null,
      PostCode: (item.Company_PostCode as string) || null,
      ContactCount: (item.Company_ContactCount as number) || 0,
      RequirementCount: (item.Company_RequirementCount as number) || 0,
      RequirementClientCount: (item.Company_RequirementClientCount as number) || 0,
      PlacementClientCount: (item.Company_PlacementClientCount as number) || 0,
      NoteHistoryCount: (item.Company_NoteHistoryCount as number) || 0
    };
    
    // Pass through dynamic columns (DynamicColumn_*) for grid display
    for (const key of Object.keys(item)) {
      if (key.startsWith('DynamicColumn_')) {
        (result as Record<string, unknown>)[key] = item[key];
      }
    }
    
    return result;
  }

  /**
   * Search companies with pagination, filters, and optional Boolean Search Tree
   * POST /Companies/List
   * 
   * @param params Search parameters including optional SearchTreeJson for complex queries
   * @example
   * // Simple search
   * trisysApi.searchCompanies({ PageNumber: 1, RecordsPerPage: 10, CompanyName: 'Acme' });
   * 
   * // Boolean Search Tree
   * trisysApi.searchCompanies({
   *   PageNumber: 1,
   *   RecordsPerPage: 10,
   *   SearchTreeEnabled: true,
   *   SearchTreeJson: '{"EntityName":"Company","RecordId":0,"Tree":{"condition":"AND","rules":[...]}}',
   *   EntitySearchId: 53
   * });
   */
  async searchCompanies(params: CompanySearchParams): Promise<CompanySearchResponse> {
    logger.debug('Searching companies', { 
      ...params, 
      SearchTreeJson: params.SearchTreeJson ? '[SearchTree]' : undefined 
    });
    
    // Call /Companies/List which returns List[] with Company_ prefixed fields
    const response = await this.request<{
      List: Record<string, unknown>[];
      TotalRecordCount: number;
      TotalPageCount: number;
      FirstRowNumber: number;
      LastRowNumber: number;
      PageNumber: number;
      RecordsPerPage: number;
      SortColumnName: string | null;
      SortAscending: boolean;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Companies/List', {
      method: 'POST',
      body: JSON.stringify(params)
    });
    
    // Transform response to expected CompanySearchResponse format
    const companies = (response.List || []).map(item => this.transformCompanyListItem(item));
    
    logger.debug('Companies search complete', { 
      count: companies.length, 
      total: response.TotalRecordCount 
    });
    
    return {
      Companies: companies,
      TotalRecordCount: response.TotalRecordCount,
      TotalPageCount: response.TotalPageCount,
      FirstRowNumber: response.FirstRowNumber,
      LastRowNumber: response.LastRowNumber,
      PageNumber: response.PageNumber,
      RecordsPerPage: response.RecordsPerPage,
      SortColumnName: response.SortColumnName,
      SortAscending: response.SortAscending,
      Success: response.Success,
      ErrorMessage: response.ErrorMessage
    };
  }

  /**
   * Read a single company by ID
   * POST /company/read
   */
  async readCompany(companyId: number): Promise<Company> {
    logger.debug('Reading company', { companyId });
    
    const response = await this.request<CompanyReadResponse>('/company/read', {
      method: 'POST',
      body: JSON.stringify({ CompanyId: companyId })
    });
    
    return response.Company;
  }

  /**
   * List addresses for a company (for address selection)
   * POST /Company/ListAddresses
   * 
   * @param contactId The contact ID (used to get the company)
   * @param pageNumber Page number (1-based)
   * @param recordsPerPage Records per page
   * @returns Paginated list of company addresses
   */
  async listCompanyAddresses(
    contactId: number,
    pageNumber: number = 1,
    recordsPerPage: number = 10
  ): Promise<{
    List: Array<{
      AddressId: number;
      Street: string;
      City: string;
      County: string;
      PostCode: string;
      Country: string;
      TelNo: string | null;
      FaxNo: string | null;
      AddressType: string;
      CompanyId: number;
    }>;
    TotalRecordCount: number;
    TotalPageCount: number;
    PageNumber: number;
    RecordsPerPage: number;
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    logger.debug('Listing company addresses', { contactId, pageNumber, recordsPerPage });
    
    const response = await this.request<{
      List: Array<{
        AddressId: number;
        Street: string;
        City: string;
        County: string;
        PostCode: string;
        Country: string;
        TelNo: string | null;
        FaxNo: string | null;
        AddressType: string;
        CompanyId: number;
      }>;
      TotalRecordCount: number;
      TotalPageCount: number;
      PageNumber: number;
      RecordsPerPage: number;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Company/ListAddresses', {
      method: 'POST',
      body: JSON.stringify({
        PageNumber: pageNumber,
        RecordsPerPage: recordsPerPage,
        CompanyId: 0,  // API uses ContactId to get the company
        ContactId: contactId,
        IgnoreAddressId: 0,
        StreetNewlineSeparator: ', '
      })
    });
    
    if (!response.Success) {
      throw new Error(response.ErrorMessage || 'Failed to list addresses');
    }
    
    return response;
  }

  /**
   * Assign a company to a contact
   * POST /Contact/AssignCompany
   * 
   * @param contactId The contact to assign the company to
   * @param companyId The company to assign
   * @param addressId Optional address ID (0 to use default company address)
   * @param operation Operation type (1 = assign)
   * @returns Updated company address info
   */
  async assignCompanyToContact(
    contactId: number, 
    companyId: number, 
    addressId: number = 0,
    operation: number = 1
  ): Promise<{
    Address: {
      AddressId: number;
      Street: string;
      City: string;
      County: string;
      PostCode: string;
      Country: string;
      TelNo: string;
      FaxNo: string;
    };
    CompanyName: string;
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    logger.debug('Assigning company to contact', { contactId, companyId, addressId, operation });
    
    const response = await this.request<{
      Address: {
        AddressId: number;
        Street: string;
        City: string;
        County: string;
        PostCode: string;
        Country: string;
        TelNo: string;
        FaxNo: string;
      };
      CompanyName: string;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Contact/AssignCompany', {
      method: 'POST',
      body: JSON.stringify({
        ContactId: contactId,
        CompanyId: companyId,
        AddressId: addressId,
        Operation: operation
      })
    });
    
    if (!response.Success) {
      throw new Error(response.ErrorMessage || 'Failed to assign company');
    }
    
    return response;
  }

  /**
   * Post Code Lookup - Search for addresses by postcode
   * POST /PostCode/Lookup
   * 
   * @param postCode Partial or full postcode to search
   * @param internalDatabaseOnly Whether to only search internal database
   * @returns List of matching addresses (max 1024)
   */
  async lookupPostCode(
    postCode: string,
    internalDatabaseOnly: boolean = false
  ): Promise<{
    Addresses: Array<{
      AddressId: number;
      Street: string;
      City: string;
      County: string;
      PostCode: string;
      Country: string;
      TelNo: string | null;
      FaxNo: string | null;
      Display: string | null;
      Type: string | null;
    }>;
    Address: null;
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    logger.debug('Looking up postcode', { postCode, internalDatabaseOnly });
    
    const response = await this.request<{
      Addresses: Array<{
        AddressId: number;
        Street: string;
        City: string;
        County: string;
        PostCode: string;
        Country: string;
        TelNo: string | null;
        FaxNo: string | null;
        Display: string | null;
        Type: string | null;
      }>;
      Address: null;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/PostCode/Lookup', {
      method: 'POST',
      body: JSON.stringify({
        PostCode: postCode,
        InternalDatabaseOnly: internalDatabaseOnly
      })
    });
    
    if (!response.Success) {
      throw new Error(response.ErrorMessage || 'Failed to lookup postcode');
    }
    
    return response;
  }

  /**
   * Add Address to Company - Creates a new address and assigns to contact
   * POST /Company/AddAddress
   * 
   * @param contactId The contact ID to assign the new address to
   * @param address The address details
   * @returns The created address with AddressId and CompanyName
   */
  async addCompanyAddress(
    contactId: number,
    address: {
      AddressType: string;
      Street: string;
      City: string;
      County: string;
      PostCode: string;
      Country: string;
      TelNo: string;
    }
  ): Promise<{
    Address: {
      AddressId: number;
      AddressType: string;
      Street: string;
      City: string;
      County: string;
      PostCode: string;
      Country: string;
      TelNo: string;
      FaxNo: string | null;
      Display: string | null;
      Type: string | null;
    };
    CompanyName: string;
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    const requestBody = {
      CompanyId: 0, // API uses ContactId to determine company
      ContactId: contactId,
      Address: {
        AddressId: 0, // 0 for new address
        AddressType: address.AddressType,
        Street: address.Street,
        City: address.City,
        County: address.County,
        PostCode: address.PostCode,
        Country: address.Country,
        TelNo: address.TelNo
      }
    };
    
    console.log('🏠 [API] /Company/AddAddress REQUEST:', JSON.stringify(requestBody, null, 2));
    
    const response = await this.request<{
      Address: {
        AddressId: number;
        AddressType: string;
        Street: string;
        City: string;
        County: string;
        PostCode: string;
        Country: string;
        TelNo: string;
        FaxNo: string | null;
        Display: string | null;
        Type: string | null;
      };
      CompanyName: string;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Company/AddAddress', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    console.log('🏠 [API] /Company/AddAddress RESPONSE:', JSON.stringify(response, null, 2));
    
    if (!response.Success) {
      throw new Error(response.ErrorMessage || 'Failed to add address');
    }
    
    return response;
  }

  /**
   * Add a new company with address
   * POST /Company/Add
   * 
   * @param companyName The name of the new company
   * @param contactId The contact ID to assign the new company to
   * @param address The company's address details
   * @returns The created company ID
   */
  async addCompany(
    companyName: string,
    contactId: number,
    address: {
      AddressType: string;
      Street: string;
      City: string;
      County: string;
      PostCode: string;
      Country: string;
      TelNo: string;
    }
  ): Promise<{
    CompanyId: number;
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    const requestBody = {
      CompanyName: companyName,
      ContactId: contactId,
      Address: {
        AddressId: 0, // 0 for new address
        AddressType: address.AddressType,
        Street: address.Street,
        City: address.City,
        County: address.County,
        PostCode: address.PostCode,
        Country: address.Country,
        TelNo: address.TelNo
      }
    };
    
    console.log('🏢 [API] /Company/Add REQUEST:', JSON.stringify(requestBody, null, 2));
    
    const response = await this.request<{
      CompanyId: number;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Company/Add', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    console.log('🏢 [API] /Company/Add RESPONSE:', JSON.stringify(response, null, 2));
    
    if (!response.Success) {
      throw new Error(response.ErrorMessage || 'Failed to add company');
    }
    
    return response;
  }

  /**
   * List addresses and contacts for a company
   * POST /Company/ListAddressesAndContacts
   * 
   * @param companyId The company ID to get addresses and contacts for
   * @param pageNumber Page number (1-based)
   * @param recordsPerPage Number of records per page
   * @param sortColumn Column to sort by (optional)
   * @param sortDirection Sort direction ('asc' or 'desc')
   * @returns Paginated list of addresses and contacts
   */
  async listAddressesAndContacts(
    companyId: number,
    pageNumber: number = 1,
    recordsPerPage: number = 20,
    sortColumn: string | null = null,
    sortDirection: 'asc' | 'desc' = 'asc'
  ): Promise<{
    List: Array<{
      CompanyAddress: string;
      Contact: string;
      ContactId: number;
      AddressId: number;
      AddressType: string;
      JobTitle: string;
      WorkTelNo: string;
      Contact_ContactType: string;
      Contact_EMail: string | null;
      Contact_Status: string;
      AddressUsed: boolean;
      AddressAdded: string;
      Company_Name: string;
      Company_IndustryId: number | null;
      Company_Industry: string | null;
    }>;
    TotalRecordCount: number;
    TotalPageCount: number;
    PageNumber: number;
    RecordsPerPage: number;
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    const requestBody = {
      CompanyId: companyId,
      PageNumber: pageNumber,
      RecordsPerPage: recordsPerPage,
      SortColumnName: sortColumn,
      SortAscending: sortDirection === 'asc'
    };
    
    console.log('🏢 [API] /Company/ListAddressesAndContacts REQUEST:', JSON.stringify(requestBody, null, 2));
    
    const response = await this.request<{
      List: Array<{
        CompanyAddress: string;
        Contact: string;
        ContactId: number;
        AddressId: number;
        AddressType: string;
        JobTitle: string;
        WorkTelNo: string;
        Contact_ContactType: string;
        Contact_EMail: string | null;
        Contact_Status: string;
        AddressUsed: boolean;
        AddressAdded: string;
        Company_Name: string;
        Company_IndustryId: number | null;
        Company_Industry: string | null;
      }>;
      TotalRecordCount: number;
      TotalPageCount: number;
      PageNumber: number;
      RecordsPerPage: number;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Company/ListAddressesAndContacts', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    console.log('🏢 [API] /Company/ListAddressesAndContacts RESPONSE:', JSON.stringify(response, null, 2));
    
    if (!response.Success) {
      throw new Error(response.ErrorMessage || 'Failed to list addresses and contacts');
    }
    
    return response;
  }
}

// Export singleton instance
export const trisysApi = new TriSysApiService();
